import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const SESSION_KEY = "p1_chat_session";
const GREETING =
  "Hey, I'm Max — your Platinum 1 advisor. Ask me anything about the certification, pricing, or whether it's right for you.";

function getSession() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g);
  return parts.map((p, i) => {
    const bold = p.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link)
      return (
        <a key={i} href={link[2]} className="text-orange-600 underline font-semibold">
          {link[1]}
        </a>
      );
    return p;
  });
}

export default function SalesChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(getSession());

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/chat/sales/${sessionId.current}`)
      .then((r) => r.json())
      .then((d) => d.messages?.length && setMessages([{ role: "assistant", content: GREETING }, ...d.messages]))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat/sales`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          const evt = JSON.parse(payload);
          const chunk = evt.delta ?? evt.error ?? "";
          setMessages((m) => {
            const copy = [...m];
            copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Connection issue — please try again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-[60]" data-testid="sales-chat-widget">
      {open && (
        <div className="mb-3 w-[360px] max-w-[calc(100vw-2.5rem)] h-[520px] max-h-[75vh] bg-white border border-slate-200 shadow-2xl rounded-sm flex flex-col" data-testid="sales-chat-panel">
          <div className="bg-slate-950 text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-display font-bold text-sm tracking-tight">Max · Platinum 1 Advisor</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-[0.2em] font-bold">Usually replies instantly</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="sales-chat-close" className="p-1 hover:bg-white/10 rounded-sm">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap rounded-sm ${
                    m.role === "user" ? "bg-orange-600 text-white" : "bg-white border border-slate-200 text-slate-800"
                  }`}
                >
                  {m.content ? renderInline(m.content) : <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
          <form onSubmit={send} className="border-t border-slate-200 p-2 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about pricing, modules, refunds…"
              data-testid="sales-chat-input"
              className="flex-1 border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
            <button type="submit" disabled={busy || !input.trim()} data-testid="sales-chat-send" className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-3 rounded-sm">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="sales-chat-toggle"
        className="ml-auto flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white pl-4 pr-5 py-3 rounded-full shadow-xl transition-colors"
      >
        <MessageCircle className="w-5 h-5 text-orange-500" />
        <span className="text-sm font-semibold">{open ? "Close" : "Questions? Ask Max"}</span>
      </button>
    </div>
  );
}
